import { Helmet } from "react-helmet"
import React, { useEffect, useMemo, useState } from "react"
import { useLocation, useNavigate, useParams } from "react-router"

import Config from "../../api/Config"
import { L0GXP } from "../../api/Registry"
import { ErrorState } from "../errors/ErrorState"
import HttpServices from "../../services/HttpServices"
import ReactTable from "../../lib/fragments/ReactTable"
import { EmptyResultSet } from "../errors/EmptyResultSet"
import { ModuleBreadCrumb } from "../../lib/fragments/ModuleBreadCrumb"
import { DateFormating, generateUniqueId, getStandardRoute, readFromCookie } from "../../utils/Helpers"
import { ExplorerDetails } from "./ExplorerDetails"
import { COOKIE_KEYS } from "../../utils/Constants"
import { usePageContext } from "../../lib/guards/PageContext"
import clsx from "clsx"

export const ExplorerResults = () => {
    const [state, setstate] = useState({
        goToPage: '',
        loading: false,
        httpStatus: 200,
        status: 'pending',
        data: {
            manifest: [],
            links: [],
            meta: {
                to: 0,
                from: 0,
                total: 0,
                lastPage: null,
                currentPage: null,
                lastPageUrl: null,
            }
        },
        input: {
            search: '',
            account: 'Y',
            auth: '',
            last: '',
            created: '',
            status: '',
        },
        errors: {
            search: ''
        },
        show: {
            uuid: null,
            explorerDetails: false
        }
    })

    const [pageSize, setPageSize] = useState(() => {
        const cookieValue = readFromCookie(COOKIE_KEYS.PAGE_COUNT);
        const parsed = Number(cookieValue);
        return Number.isFinite(parsed) && parsed > 0 ? parsed : 25;
    });

    const [displayColumns, setDisplayColumns] = useState([
        { id: '0', key: 'e', label: 'Summary', default: true },
        { id: '1', key: 'u', label: 'Account', default: false },
        { id: '2', key: 'c', label: 'Timestamp', default: false },
        { id: '3', key: 'n', label: 'Log Type', default: false },
        { id: '4', key: 'i', label: 'IP Address', default: false },
        { id: '5', key: 'z', label: '', default: true },
    ])

    const location = useLocation()
    const navigate = useNavigate()
    const [visibleColumns, setVisibleColumns] = useState<string[]>([]);

    const logXplorerRoute = getStandardRoute('L0GXP.SEARCH_')
    const searchParams = new URLSearchParams(location.search)

    const pageTitle = "Log Xplorer"
    type ModalKeys = 'explorerDetails' | ''
    const { updateDataContext } = usePageContext();

    const breadCrumb = [
        { linkItem: false, title: 'General' },
        { linkItem: false, title: pageTitle },
    ]

    useEffect(() => {
        dataContextHandler()

        const to = searchParams.get("to")
        const from = searchParams.get("from")
        const users = searchParams.get("users")
        const events = searchParams.get("events")

        // Redirect immediately if none of the required parameters are present
        if (!to && !from && !events && !users) {
            navigate(logXplorerRoute, { replace: true })
            return
        } else {
            setVisibleColumns(displayColumns.map((c) => c.key));
            fetchLogXplorerResults(pageSize)
        }
    }, [navigate, pageSize]);

    const fetchLogXplorerResults = async (pageSize: any, navigateTo?: string | null) => {
        let { status, httpStatus, data } = state

        try {
            const routeParams = new URLSearchParams({
                ...Object.fromEntries(new URLSearchParams(location.search)),
                per_page: String(pageSize),
            });

            const baseUrl = navigateTo ? navigateTo.replace(Config.APP_CORE, '') : `${L0GXP.SEARCH}?${routeParams}`;
            const response: any = await HttpServices.httpGet(baseUrl);
            httpStatus = response.status

            if (httpStatus === 200) {
                const dump = response.data

                data.manifest = dump.data
                data.links = dump.links

                data.meta.to = dump.to
                data.meta.from = dump.from
                data.meta.total = dump.total
                data.meta.lastPage = dump.last_page
                data.meta.currentPage = dump.current_page
                data.meta.lastPageUrl = dump.last_page_url

                status = 'fulfilled'
                dataContextHandler('fulfilled')
            } else {
                status = 'rejected'
            }
        } catch (error) {
            status = 'rejected'
            httpStatus = 500
        }

        setstate((prevState: any) => ({
            ...prevState,
            status,
            httpStatus,
            loading: false,
            data: {
                ...prevState.data,
            },
        }));
    }

    const dataContextHandler = (status: string = 'pending') => {
        const baseContext = {
            title: pageTitle,
        };

        if (status !== 'fulfilled') {
            setTimeout(() => {
                updateDataContext(baseContext);
            }, 50)

            return;
        }
    }

    const columnarLedger = useMemo(
        () => [
            {
                key: 'e',
                Header: 'Summary',
                id: generateUniqueId('Summary'),
                accessor: (manifest: { e: any, n: any }) => (
                    <span className="w-full sm:flex-1">
                        {
                            manifest.n === 'error' ? (
                                <span className="inline-flex items-center text-xs text-red-500 dark:text-red-400 uppercase">
                                    {manifest.e}
                                </span>
                            ) : (
                                <span className="text-sm text-slate-700 dark:text-slate-200">
                                    {manifest.e}
                                </span>
                            )
                        }
                    </span>
                ),
            },
            {
                key: 'u',
                Header: 'Account',
                id: generateUniqueId('User'),
                accessor: (manifest: { u: any }) => (
                    <span className="w-full sm:flex-1">
                        {
                            manifest.u.trim() ? (
                                <span className="inline-flex items-center text-sm text-slate-500 dark:text-slate-400">
                                    {manifest.u}
                                </span>
                            ) : (
                                <span className="inline-flex items-center text-xs text-red-500 dark:text-red-400 uppercase">
                                    Unknown User
                                </span>
                            )
                        }
                    </span>
                ),
            },
            {
                key: 'n',
                Header: 'Log Type',
                id: generateUniqueId('Status'),
                accessor: (manifest: { n: any }) => (
                    <div className="hidden lg:flex flex-wrap align-middle items-center gap-4 w-full text-slate-500 dark:text-slate-400 pt-1 min-w-0 uppercase">
                        <span className={clsx(
                            manifest.n === 'info' && 'bg-blue-100 dark:bg-blue-800/40 text-blue-500 dark:text-blue-400',
                            manifest.n === 'warn' && 'bg-amber-100 dark:bg-amber-800/40 text-amber-500 dark:text-amber-400',
                            manifest.n === 'error' && 'bg-red-100 dark:bg-red-800/40 text-red-500 dark:text-red-400 uppercase',
                            manifest.n === 'notice' && 'bg-purple-100 dark:bg-purple-800/40 text-purple-500 dark:text-purple-400',
                            "rounded-md px-1.5 py-1 text-xs ring-0 ring-inset"
                        )}>
                            {manifest.n}
                        </span>
                    </div>
                )
            },
            {
                key: 'c',
                Header: 'Timestamp',
                id: generateUniqueId('Timestamp'),
                accessor: (manifest: { c: any }) => {
                    function lastLoggedInFunc() {
                        const lastLoggedIn = DateFormating(manifest.c, 'DD MMM YYYY, hh:mm A [UTC]')
                        const [lastDate, lastTime] = lastLoggedIn.split(",");

                        return <>
                            {lastDate} <span className="md:hidden lg:inline-block">
                                {lastTime}
                            </span>
                        </>
                    }

                    return (
                        (
                            <div className="hidden md:flex flex-wrap align-middle items-center w-full text-slate-500 dark:text-slate-400 pt-1 min-w-0">
                                <span className="w-full sm:flex-1 text-slate-500 dark:text-slate-400 truncate text-ellipsis overflow-hidden whitespace-nowrap">
                                    {
                                        manifest.c ? (
                                            lastLoggedInFunc()
                                        ) : (
                                            'Invalid Date Time'
                                        )
                                    }
                                </span>
                            </div>
                        )
                    )
                }
            },
            {
                key: 'i',
                Header: 'IP Address',
                id: generateUniqueId('IP Address'),
                accessor: (manifest: { i: any }) => (
                    <span className="w-full sm:flex-1">
                        {manifest.i}
                    </span>
                ),
            },
            {
                key: 'z',
                Header: '',
                id: generateUniqueId('-'),
                accessor: (data: { g: any }) => (
                    // <button type="button" onClick={() => toggleModal('explorerDetails', true, data.uuid)} className="text-orange-500 dark:text-orange-400 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    //     Xplore
                    // </button>

                    <div className="lg:flex flex flex-row-reverse align-middle items-center gap-x-2 w-full text-slate-500 dark:text-slate-400 pt-1 min-w-0 md:opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                        <div className="flex-none">
                            <button type="button" className="flex items-center align-middle gap-x-2" onClick={() => toggleModal('explorerDetails', true, data.g)} >
                                <span className="fa-light fa-eye fa-lg"></span>

                                <span className="md:block hidden text-xs">
                                    View
                                </span>
                            </button>
                        </div>
                    </div>
                ),
            },
        ], []
    )

    const reorderedDisplayColumns = async (newDisplayList: any[]) => {
        let { loading } = state
        if (loading) return

        setDisplayColumns(newDisplayList);

        setVisibleColumns(prevVisible => {
            // Extract keys from the new order
            const orderedKeys = newDisplayList.map(c => c.key);

            // Keep only keys that were already visible
            const filteredAndOrdered = orderedKeys.filter(key => prevVisible.includes(key));

            // Return the reordered, valid list
            return filteredAndOrdered;
        });

        console.log('Reordered →', newDisplayList);
    }

    const onPageSizeChangeHandler = (pageSize: any) => {
        let { loading } = state
        if (loading) return

        setstate((prevState: any) => ({
            ...prevState,
            loading: true,
        }));

        fetchLogXplorerResults(pageSize)
    }

    const toggleColumn = (key: string) => {
        if (key === "e") return;

        setVisibleColumns((prev) => {
            let updated: string[];

            if (prev.includes(key)) {
                // remove key
                updated = prev.filter((col) => col !== key);
            } else {
                // add but enforce max 6 (inclusive of z)
                if (prev.length >= 6) {
                    return prev; // no change
                }

                updated = [...prev, key];
            }

            // always put z last if it exists
            if (updated.includes("z")) {
                updated = updated.filter((col) => col !== "z");
                updated.push("z");
            }

            return updated;
        });
    };

    const pageNavigationHandler = (navigateTo: string) => {
        let { loading } = state
        if (loading) return

        setstate((prevState: any) => ({
            ...prevState,
            loading: true,
        }));

        fetchLogXplorerResults(pageSize, navigateTo)
    }



    // const goToPageHandler = (navigateTo: any) => {
    //     let { loading, quant } = state
    //     if (loading) return

    //     setstate((prevState: any) => ({
    //         ...prevState,
    //         loading: true,
    //     }));

    //     navigateTo = navigateTo.replace(Config.APP_CORE, "")
    //     fetchLogXplorerResults(quant, navigateTo)
    // }

    const searchAgainHandler = () => {
        const logXplorerRoute = getStandardRoute('L0GXP.SEARCH_')

        return navigate(logXplorerRoute, { replace: false });
    }

    const toggleModal = (key: ModalKeys, value: boolean, uuid?: any) => {
        setstate((prevState: any) => ({
            ...prevState,
            show: {
                ...prevState.show,
                [key]: value,
                uuid: uuid,
            },
        }))
    }

    return (
        <React.Fragment>
            <Helmet>
                <title>{pageTitle}</title>
            </Helmet>

            <div className="flex flex-col gap-x-3 w-full mx-auto h-full">
                <div className="w-full py-2 border- border-slate-200 dark:border-slate-600 lg:px-8 md:px-8 px-5 flex flex-col">
                    <ModuleBreadCrumb breadCrumb={breadCrumb} />
                </div>

                {
                    state.status === 'rejected' ? (
                        <div className="py-2 px-6 w-full mx-auto flex-1 flex flex-col justify-center">
                            <ErrorState
                                code={state.httpStatus}
                            />
                        </div>
                    ) : state.status === 'fulfilled' ? (
                        <div className="md:h-full lg:h-full flex flex-col">
                            <div className="header-title align-middle title-transition lg:px-6 px-5 pt-3">
                                <div className="md:hidden flex flex-col pb-3 gap-y-3">
                                    <div className="flex-1">
                                        <h3 className="text-slate-900 dark:text-white text-xl md:font-normal font-medium tracking-wide">
                                            {pageTitle}
                                        </h3>
                                    </div>
                                </div>
                            </div>

                            <div className="w-full pb-7 lg:px-6 md:px-6 px-5">
                                {
                                    state.data.manifest.length < 1 ? (
                                        <EmptyResultSet
                                            description={
                                                <div className="text-sm flex flex-col items-center md:space-y-2 space-y-3">
                                                    <span className="block">No results found.</span>
                                                    <span className="block text-slate-500 dark:text-slate-400 pb-2">Try adjusting your search to find what you're looking for.</span>

                                                    <button type="button" onClick={searchAgainHandler} className="text-orange-500 dark:text-orange-500 text-sm hover:underline hover:text-orange-700 dark:hover:text-orange-600 focus:outline-none focus:ring-0 focus:ring-offset-2">
                                                        Search Again
                                                    </button>
                                                </div>
                                            }
                                        />
                                    ) : (
                                        <div className="pb-28">
                                            <ReactTable
                                                columns={columnarLedger}
                                                manifest={state.data.manifest}
                                                loading={state.loading}
                                                pageHandler={onPageSizeChangeHandler}
                                                meta={state.data.meta}
                                                links={state.data.links}
                                                navigation={pageNavigationHandler}
                                                input={state.input}
                                                showPageSize={true}
                                                visibleColumns={visibleColumns}
                                                toggleColumn={toggleColumn}
                                                display={displayColumns}
                                                reorderfunc={reorderedDisplayColumns}
                                            />
                                        </div>
                                    )
                                }
                            </div>
                        </div>
                    ) : (
                        <div className="inset-0 bg-white dark:bg-slate-800 flex items-enter justify-center pb-28">
                            <table className="w-full divide-y divide-slate-200 dark:divide-slate-600 text-sm mt-4">
                                <thead>
                                    <tr key={`--sktln-thead`}>
                                        {
                                            columnarLedger.map((column: any, index: any) => (
                                                <th key={`--sktln-thead-col-${index}`} className={`px-3 py-3.5 bg-slate-200 dark:bg-slate-900 text-left text-sm text-slate-400 dark:text-slate-200 tracking-wider `}>
                                                    <div className="h-5 bg-gray-300 dark:bg-slate-700 rounded w-full"></div>
                                                </th>
                                            ))
                                        }
                                    </tr>
                                </thead>

                                <tbody className="animate-pulse">
                                    {
                                        Array.from({ length: pageSize }).map((_, key) => (
                                            <tr key={`--sktln-tbody-row-${key}`} className="w-full border-b border-slate-300 dark:border-slate-600">
                                                {
                                                    columnarLedger.map((__, index) => (
                                                        <td key={`--sktln-thead-col-${index}`} className={`px-3 py-3 text-left text-sm text-slate-400 dark:text-slate-200 tracking-wider `}>
                                                            <div className={`h-4 ${index % 2 === 0 ? 'bg-gray-200 dark:bg-slate-900' : 'bg-gray-300 dark:bg-slate-700'} rounded w-full`}></div>
                                                        </td>
                                                    ))
                                                }
                                            </tr>
                                        ))
                                    }
                                </tbody>
                            </table>
                        </div>
                    )
                }
            </div>

            <ExplorerDetails
                uuid={state.show.uuid}
                show={state.show.explorerDetails}
                showOrHide={() => toggleModal('explorerDetails', false)}
            />



        </React.Fragment>
    )
}